import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase, Listing } from '@/lib/supabase';
import { formatPrice } from '@/lib/utils';
import { AdminAnalytics } from './AdminAnalytics';
import { 
  CheckCircle2, 
  XCircle, 
  Loader2, 
  Eye, 
  MapPin, 
  Clock, 
  Calendar, 
  ArrowLeft, 
  ShieldAlert,
  LayoutGrid,
  ListFilter,
  BarChart3,
  TrendingUp,
  AlertCircle,
  Mail,
  X,
  Archive,
  Layers,
  AlertTriangle,
  Zap
} from 'lucide-react';

type TabType = 'pending' | 'approved' | 'rejected' | 'rented' | 'sold';
type ViewType = 'listings' | 'analytics';

export function AdminDashboardPage() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('pending');
  const [currentView, setCurrentView] = useState<ViewType>('listings');
  
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'warning';
    message: string;
  } | null>(null);
  
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    rented: 0,
    sold: 0
  });

  useEffect(() => {
    if (!authLoading) {
      if (!user || !isAdmin) {
        navigate('/');
        return;
      }
      if (currentView === 'listings') {
        fetchListings();
        fetchStats();
      }
    }
  }, [user, isAdmin, authLoading, navigate, activeTab, currentView]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  async function fetchStats() {
    try {
      const { data, error } = await supabase.from('listings').select('status');
      if (error) throw error;
      if (data) {
        const newStats = data.reduce((acc, curr) => {
          acc.total++;
          if (curr.status === 'pending') acc.pending++;
          if (curr.status === 'approved') acc.approved++;
          if (curr.status === 'rejected') acc.rejected++;
          if (curr.status === 'rented') acc.rented++;
          if (curr.status === 'sold') acc.sold++;
          return acc;
        }, { total: 0, pending: 0, approved: 0, rejected: 0, rented: 0, sold: 0 });
        setStats(newStats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }

  async function fetchListings() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('listings')
        .select('*')
        .eq('status', activeTab)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setListings(data || []);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  }

  const showNotification = (type: 'success' | 'error' | 'warning', message: string) => {
    setNotification({ type, message });
  };

  const handleStatusChange = async (listing: Listing, newStatus: 'approved' | 'rejected') => {
    setActionLoading(listing.id);
    try {
      const { error } = await supabase
        .from('listings')
        .update({ status: newStatus })
        .eq('id', listing.id);

      if (error) throw error;

      // La base de datos envía el email automáticamente al detectar el cambio de estado.

      setListings(prev => prev.filter(l => l.id !== listing.id));
      fetchStats(); 
      
      const actionText = newStatus === 'approved' ? 'aprobado' : 'rechazado';
      showNotification('success', `Anuncio ${actionText} correctamente. El usuario recibirá un email.`);
      
    } catch (error: any) {
      console.error(`Error updating listing status:`, error);
      showNotification('error', `Error al actualizar: ${error.message || 'Inténtalo de nuevo'}`);
    } finally {
      setActionLoading(null);
    }
  };

  if (authLoading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-10 h-10 animate-spin text-blue-600" /></div>;

  return (
    <div className="min-h-screen bg-slate-50 pb-12 relative">
        {notification && (
          <div className={`fixed top-20 right-4 z-50 p-4 rounded-xl shadow-xl border flex items-center gap-3 max-w-md animate-in slide-in-from-right duration-300 ${
            notification.type === 'success' ? 'bg-white border-green-200 text-green-800' : 
            notification.type === 'warning' ? 'bg-white border-amber-200 text-amber-800' :
            'bg-white border-red-200 text-red-800'
          }`}>
            <div className={`p-2 rounded-full ${
              notification.type === 'success' ? 'bg-green-100' : 
              notification.type === 'warning' ? 'bg-amber-100' :
              'bg-red-100'
            }`}>
              {notification.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : 
               notification.type === 'warning' ? <AlertTriangle className="w-5 h-5" /> :
               <AlertCircle className="w-5 h-5" />}
            </div>
            <p className="font-medium text-sm">{notification.message}</p>
            <button onClick={() => setNotification(null)} className="ml-auto text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="bg-slate-900 text-white shadow-md sticky top-0 z-20">
            <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="bg-amber-500/20 p-2 rounded-lg border border-amber-500/30">
                        <ShieldAlert className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <h1 className="text-xl font-bold">Panel de Control</h1>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-slate-400">Admin: {user?.email}</p>
                        <span className="text-slate-600">•</span>
                        <div className="flex items-center gap-1.5 px-2 py-0.5 bg-green-900/40 border border-green-500/30 rounded-full">
                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                            <span className="text-[10px] font-bold text-green-400 uppercase tracking-wide">Emails Activos</span>
                        </div>
                      </div>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <Link to="/" className="text-slate-300 hover:text-white font-medium text-sm flex items-center gap-2 bg-slate-800 px-4 py-2 rounded-lg transition-colors">
                        <ArrowLeft className="w-4 h-4" />
                        Volver a la web
                    </Link>
                </div>
            </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="flex gap-4 mb-8 border-b border-slate-200 pb-1">
                <button
                    onClick={() => setCurrentView('listings')}
                    className={`pb-3 px-4 font-bold text-sm flex items-center gap-2 transition-colors border-b-2 ${
                        currentView === 'listings' 
                            ? 'border-blue-600 text-blue-600' 
                            : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                >
                    <Layers className="w-4 h-4" />
                    Gestión de Anuncios
                </button>
                <button
                    onClick={() => setCurrentView('analytics')}
                    className={`pb-3 px-4 font-bold text-sm flex items-center gap-2 transition-colors border-b-2 ${
                        currentView === 'analytics' 
                            ? 'border-blue-600 text-blue-600' 
                            : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                >
                    <TrendingUp className="w-4 h-4" />
                    Estadísticas Avanzadas
                </button>
            </div>

            {currentView === 'analytics' ? (
                <AdminAnalytics />
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 flex flex-col justify-center">
                        <div className="flex items-center gap-2 text-slate-500 mb-1">
                            <BarChart3 className="w-4 h-4" /> <span className="text-xs font-bold uppercase">Total</span>
                        </div>
                        <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 flex flex-col justify-center border-l-4 border-l-amber-400">
                        <div className="flex items-center gap-2 text-amber-600 mb-1">
                            <Clock className="w-4 h-4" /> <span className="text-xs font-bold uppercase">Pendientes</span>
                        </div>
                        <p className="text-2xl font-bold text-slate-900">{stats.pending}</p>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 flex flex-col justify-center border-l-4 border-l-green-500">
                        <div className="flex items-center gap-2 text-green-600 mb-1">
                            <CheckCircle2 className="w-4 h-4" /> <span className="text-xs font-bold uppercase">Publicados</span>
                        </div>
                        <p className="text-2xl font-bold text-slate-900">{stats.approved}</p>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 flex flex-col justify-center border-l-4 border-l-slate-500">
                        <div className="flex items-center gap-2 text-slate-600 mb-1">
                            <Archive className="w-4 h-4" /> <span className="text-xs font-bold uppercase">Cerrados</span>
                        </div>
                        <p className="text-2xl font-bold text-slate-900">{stats.rented + stats.sold}</p>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 flex flex-col justify-center border-l-4 border-l-red-500">
                        <div className="flex items-center gap-2 text-red-600 mb-1">
                            <XCircle className="w-4 h-4" /> <span className="text-xs font-bold uppercase">Rechazados</span>
                        </div>
                        <p className="text-2xl font-bold text-slate-900">{stats.rejected}</p>
                    </div>
                    </div>

                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                    <div className="flex bg-white p-1 rounded-xl shadow-sm border border-slate-200 w-full sm:w-auto overflow-x-auto">
                        <button
                        onClick={() => setActiveTab('pending')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${
                            activeTab === 'pending' 
                            ? 'bg-amber-50 text-amber-700 shadow-sm ring-1 ring-amber-200' 
                            : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                        }`}
                        >
                        <Clock className="w-4 h-4" />
                        Pendientes
                        {stats.pending > 0 && (
                            <span className="ml-1 bg-amber-200 text-amber-800 text-xs px-2 py-0.5 rounded-full">
                            {stats.pending}
                            </span>
                        )}
                        </button>
                        <button
                        onClick={() => setActiveTab('approved')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${
                            activeTab === 'approved' 
                            ? 'bg-green-50 text-green-700 shadow-sm ring-1 ring-green-200' 
                            : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                        }`}
                        >
                        <CheckCircle2 className="w-4 h-4" />
                        Publicados
                        </button>
                        <button
                        onClick={() => setActiveTab('rented')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${
                            activeTab === 'rented' 
                            ? 'bg-slate-100 text-slate-800 shadow-sm ring-1 ring-slate-300' 
                            : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                        }`}
                        >
                        <Archive className="w-4 h-4" />
                        Alquilados
                        </button>
                        <button
                        onClick={() => setActiveTab('sold')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${
                            activeTab === 'sold' 
                            ? 'bg-slate-100 text-slate-800 shadow-sm ring-1 ring-slate-300' 
                            : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                        }`}
                        >
                        <Archive className="w-4 h-4" />
                        Vendidos
                        </button>
                        <button
                        onClick={() => setActiveTab('rejected')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${
                            activeTab === 'rejected' 
                            ? 'bg-red-50 text-red-700 shadow-sm ring-1 ring-red-200' 
                            : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                        }`}
                        >
                        <XCircle className="w-4 h-4" />
                        Rechazados
                        </button>
                    </div>
                    </div>

                    {loading ? (
                    <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-slate-200 border-dashed">
                        <Loader2 className="w-10 h-10 animate-spin text-blue-600 mb-4" />
                        <p className="text-slate-500">Cargando anuncios...</p>
                    </div>
                    ) : listings.length === 0 ? (
                        <div className="bg-white rounded-2xl p-16 text-center border border-slate-200 border-dashed shadow-sm">
                        <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
                            {activeTab === 'pending' ? <CheckCircle2 className="w-10 h-10 text-green-500" /> : <ListFilter className="w-10 h-10 text-slate-300" />}
                        </div>
                        <h3 className="text-2xl font-bold text-slate-900 mb-2">
                            {activeTab === 'pending' ? '¡Todo al día!' : 'No hay anuncios aquí'}
                        </h3>
                        <p className="text-slate-500 text-lg">
                            {activeTab === 'pending' 
                            ? 'No hay anuncios pendientes de revisión.' 
                            : `No se encontraron anuncios en estado "${activeTab}".`}
                        </p>
                        </div>
                    ) : (
                        <div className="grid gap-6">
                        {listings.map((listing) => (
                            <div key={listing.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col md:flex-row hover:shadow-md transition-shadow group">
                            <div className="w-full md:w-72 h-56 md:h-auto bg-slate-100 relative shrink-0 overflow-hidden">
                                {listing.images && listing.images.length > 0 ? (
                                <img 
                                    src={listing.images[0]} 
                                    alt={listing.title} 
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                                ) : (
                                <div className="w-full h-full flex items-center justify-center text-slate-400 bg-slate-100">
                                    <span className="text-sm">Sin imagen</span>
                                </div>
                                )}
                                <div className="absolute top-3 left-3 bg-white/95 backdrop-blur px-3 py-1 rounded-md text-xs font-bold uppercase tracking-wide shadow-sm">
                                {listing.operation_type === 'rent' ? 'Alquiler' : 'Venta'}
                                </div>
                            </div>

                            <div className="p-6 flex-1 flex flex-col justify-between">
                                <div>
                                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-2 mb-3">
                                    <h3 className="text-xl font-bold text-slate-900 leading-tight">{listing.title}</h3>
                                    <span className="text-xl font-bold text-blue-600 whitespace-nowrap">{formatPrice(listing.price)}</span>
                                </div>
                                
                                <div className="flex flex-wrap gap-4 text-sm text-slate-500 mb-5">
                                    <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded border border-slate-100">
                                    <MapPin className="w-4 h-4 text-slate-400" />
                                    {listing.city}, {listing.province}
                                    </div>
                                    <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded border border-slate-100">
                                    <Calendar className="w-4 h-4 text-slate-400" />
                                    {new Date(listing.created_at).toLocaleDateString()}
                                    </div>
                                    {activeTab === 'pending' && (
                                    <div className="flex items-center gap-1.5 bg-yellow-50 text-yellow-700 px-2 py-1 rounded border border-yellow-100">
                                        <Clock className="w-4 h-4" />
                                        Espera: {Math.max(0, Math.floor((Date.now() - new Date(listing.created_at).getTime()) / (1000 * 60 * 60 * 24)))} días
                                    </div>
                                    )}
                                </div>

                                <p className="text-slate-600 line-clamp-2 mb-5 text-sm">{listing.description}</p>
                                
                                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 mb-4 text-sm">
                                    <p className="font-semibold text-slate-900 mb-2 flex items-center gap-2 text-xs uppercase tracking-wider">
                                        <LayoutGrid className="w-3 h-3" />
                                        Datos del Propietario
                                    </p>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-y-2 gap-x-4">
                                    <div className="flex flex-col">
                                        <span className="text-xs text-slate-400">Nombre</span>
                                        <span className="font-medium text-slate-700">{listing.contact_name || 'N/A'}</span>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-xs text-slate-400">Email</span>
                                        <span className="font-medium text-slate-700">{listing.contact_email || 'N/A'}</span>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-xs text-slate-400">Teléfono</span>
                                        <span className="font-medium text-slate-700">{listing.contact_phone || 'N/A'}</span>
                                    </div>
                                    </div>
                                </div>
                                </div>

                                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-4 border-t border-slate-100 mt-2">
                                <Link 
                                    to={`/listings/${listing.id}`} 
                                    target="_blank"
                                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors font-medium text-sm"
                                >
                                    <Eye className="w-4 h-4" />
                                    Ver Anuncio
                                </Link>
                                
                                <div className="sm:flex-1"></div>

                                {activeTab === 'pending' && (
                                    <>
                                        <button
                                            onClick={() => handleStatusChange(listing, 'rejected')}
                                            disabled={actionLoading === listing.id}
                                            className="flex items-center justify-center gap-2 px-5 py-2.5 bg-white border border-red-200 text-red-600 rounded-xl hover:bg-red-50 transition-colors font-medium text-sm"
                                        >
                                            {actionLoading === listing.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                                            Rechazar
                                        </button>
                                        <button
                                            onClick={() => handleStatusChange(listing, 'approved')}
                                            disabled={actionLoading === listing.id}
                                            className="flex items-center justify-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors font-bold text-sm shadow-md shadow-green-200"
                                        >
                                            {actionLoading === listing.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                                            Aprobar
                                        </button>
                                    </>
                                )}

                                {activeTab === 'approved' && (
                                    <button
                                        onClick={() => handleStatusChange(listing, 'rejected')}
                                        disabled={actionLoading === listing.id}
                                        className="flex items-center justify-center gap-2 px-5 py-2.5 bg-white border border-red-200 text-red-600 rounded-xl hover:bg-red-50 transition-colors font-medium text-sm"
                                    >
                                        {actionLoading === listing.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                                        Despublicar
                                    </button>
                                )}

                                {(activeTab === 'rejected' || activeTab === 'rented' || activeTab === 'sold') && (
                                    <button
                                        onClick={() => handleStatusChange(listing, 'approved')}
                                        disabled={actionLoading === listing.id}
                                        className="flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-bold text-sm shadow-md shadow-blue-200"
                                    >
                                        {actionLoading === listing.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                                        Republicar
                                    </button>
                                )}

                                </div>
                            </div>
                            </div>
                        ))}
                        </div>
                    )}
                </>
            )}
        </div>
    </div>
  );
}
